import React, { useContext, useRef, useState, useEffect, useCallback } from "react";
import studyReportContext from "../../../../../context/studyreport/StudyReportContext";
import MicIcon from "../../../../../components/SVGComponents/MicIcon";
import CustomToolTipHost from "../../../../../components/CustomToolTipHost/CustomToolTipHost";
import { useTranslation } from "react-i18next";
import FroalaEditor from "froala-editor";
import MicIconForLlm from "../../../../../components/SVGComponents/MicIconForLlm";
import userContext from "../../../../../context/users/UserContext";
import { isLLMAgentPresent } from "../../../../../service/MaskImageService";
import { Transforms, Editor} from "slate";
import AgenticReportingContext from "../../../../../context/agentic-reporting/AgenticReportingContext";
import MobileViewerContext from "../../../../../context/studyviewer/MobileViewerContext";
import { isMobile } from "react-device-detect";
import { getEnv } from '../../../../../helpers/EnvHelper';
import { getDeepgramToken } from '../../../../../service/DeepgramService';

const MicToolComponent = () => {
  const {
    editorRef,
    activeField,
    setObservationState,
    setFindingSentence,
    modality,
    studyDescription,
    activeTabKey
  } = useContext(studyReportContext);

  const {enableLlmReport} = useContext(userContext);

  const {
    setInputValue,
    getChatInputRef,
    isMicRecording: contextIsMicRecording,
    setIsMicRecording: setContextIsMicRecording,
    isTalking
  } = useContext(AgenticReportingContext);

  const mobileViewerCtx = useContext(MobileViewerContext);
  const setSlideReportOpen = isMobile ? mobileViewerCtx?.setSlideReportOpen : null;

  const [t] = useTranslation();
  const [localIsRecording, setLocalIsRecording] = useState(false);
  const isLlmMicActive = enableLlmReport && isLLMAgentPresent(modality, studyDescription);
  const isRecording = isLlmMicActive ? contextIsMicRecording : localIsRecording;
  const updateRecordingState = (status) => {
    if (isLlmMicActive) {
        setContextIsMicRecording(status);
    } else {
        setLocalIsRecording(status);
    }
  };
  const webSocketRef = useRef(null);
  const keepAliveRef = useRef(null);
  const mediaStreamRef = useRef(null); // Store the audio stream
  const silenceTimerRef = useRef(null); 
  const insertTextRef = useRef(null);
  const shouldBeRecordingRef = useRef(false);
  const info = (message) => console.log(message); // Logging function
  const errorLog = (message, error) => console.error(message, error);

  useEffect(() => {
    shouldBeRecordingRef.current = isRecording;
  }, [isRecording]);

  const insertText = useCallback((transcript) => {
    const text = transcript + " ";
    // LLM Chat Input Insertion
    if (isLlmMicActive) {
        const currentChatEditor = getChatInputRef ? getChatInputRef(activeTabKey) : null;
        if (activeField === "chatInput" && currentChatEditor) {
            Transforms.insertText(currentChatEditor, transcript + " ");
            setInputValue(JSON.stringify(currentChatEditor.children)); 
            return;
        }
    }

    //handle updates 
    setObservationState(prev => {
      const newState = { ...prev };
      switch(activeField) {
        case 'clinicalHistory':
          newState.clinicalHistory = (prev.clinicalHistory || '') + text;
          break;
        case 'comparison':
          newState.comparison = (prev.comparison || '') + text;
          break;
        case 'technique':
          newState.technique = (prev.technique || '') + text;
          break;
        default:
          break;
      }
      return newState;
    });

    // Handle findings here
    if (activeField === 'findings') {
      setFindingSentence(prev => (prev || '') + text);
    }

    // handle editor here
    if (!activeField && editorRef?.current?.editor) {
      editorRef.current.editor.html.insert(text);
    }
  }, [activeField, getChatInputRef, activeTabKey, setInputValue, setObservationState, setFindingSentence, editorRef]);

  // Keep ref updated
  useEffect(() => {
    insertTextRef.current = insertText;
  }, [insertText]);

  const stopRecording = () => {
    if (webSocketRef.current) {
      webSocketRef.current.close();
      webSocketRef.current = null;
    }

    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }

    // Stop all tracks on the audio stream to release the microphone
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    info("Recording stopped.");
  };

  const startRecording = async () => {
    info("Microphone clicked to start recording. ");
    const DEEPGRAM_API_URL = getEnv("REACT_APP_DEEPGRAM_API_URL");

    // The Deepgram API key lives on the backend. Mint a short-lived token
    // for this session instead; it only has to survive the handshake below.
    const accessToken = await getDeepgramToken();
    if (!accessToken) {
      errorLog("Could not obtain a transcription token.");
      updateRecordingState(false);
      return;
    }

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ 
        audio: true,
      });

      // Abort if user cancelled during the await
      if (!shouldBeRecordingRef.current) {
          console.warn("User aborted recording during connection.");
          audioStream.getTracks().forEach(t => t.stop());
          return;
      }

      // Store the audio stream in a ref to manage it later
      mediaStreamRef.current = audioStream;
      const mediaRecorder = new MediaRecorder(audioStream);

       if (mediaRecorder) {
         // Create WebSocket connection to Deepgram API
         // A browser WebSocket cannot set an Authorization header, so the
         // credential goes in Sec-WebSocket-Protocol. A temporary JWT must use
         // the "bearer" scheme - "token" is only for permanent API keys, and an
         // access_token query parameter is rejected with 401.
         const newWebSocket = new WebSocket(DEEPGRAM_API_URL, [
           "bearer",
           accessToken,
         ]);
         webSocketRef.current = newWebSocket;
        // Handle WebSocket open event
        newWebSocket.onopen = () => {
          info("WebSocket connection established.");

          mediaRecorder.addEventListener("dataavailable", (event) => {
            if (event.data.size > 0 && newWebSocket.readyState === 1) {
              newWebSocket.send(event.data);
            }
          });

          mediaRecorder.start(20);

          keepAliveRef.current = setInterval(() => {
            const keepAliveMsg = JSON.stringify({ type: "KeepAlive" });
            newWebSocket.send(keepAliveMsg);
            info("Sent KeepAlive message.");
          }, 3000);
        };
      // Handle WebSocket message event
      newWebSocket.onmessage = message => {
        const data = JSON.parse(message.data);
        const transcript = data?.channel?.alternatives?.[0]?.transcript;
        if (transcript && data.is_final && insertTextRef.current) {
          insertTextRef.current(transcript);
          resetSilenceTimer();
         }
      };
      
      // Handle WebSocket close event
        newWebSocket.onclose = () => {
          info("WebSocket connection closed.");
        };

        // Handle WebSocket error event
        newWebSocket.onerror = (error) => {
          info("WebSocket error.", { error });
          updateRecordingState(false);
        };

        // Start the silence timer initially
        resetSilenceTimer();
       }
    } catch (error) {
      errorLog("Microphone permission denied.", { error });
      updateRecordingState(false);
    }
  };

  const resetSilenceTimer = () => {
    info("Silence timer reset.");
    // Clear any existing silence timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }

    // Start a new 10-second silence timer
    silenceTimerRef.current = setTimeout(() => {
      console.log("Silence detected for 60 seconds. Stopping recording...");
      // Stop recording if no speech for 60 seconds
      updateRecordingState(false);
    }, 1000*60); // 60 seconds
  };

  useEffect(() => {
    if (isRecording) {
      if (!mediaStreamRef.current) {
        startRecording();
      }
    } else {
      stopRecording();
    }
    
    return () => {
       stopRecording();
    };
  }, [isRecording]);

  // const toggleRecording = () => {
  //   setIsRecording((prevState) => !prevState);
  //   isRecording ? stopRecording() : startRecording();
  // };

  const toggleRecording = () => {
    if (isTalking) return;
    if (isLlmMicActive) {
        setContextIsMicRecording(prev => !prev);
    } else {
        setLocalIsRecording(prev => !prev);
    }
    if (setSlideReportOpen) setSlideReportOpen(true);
  };

  const containerClassName = isLlmMicActive ? 'highlight-tool-llm' : 'topbar-toolbar-lock-warning';

  let tooltipContent = isRecording ? t("heading.mic-stop") : t("heading.mic-start");
  if (isTalking) {
      tooltipContent = "Turn off assistant to use mic";
  }

  return (
    <>
      <CustomToolTipHost
        content={tooltipContent}
        id="micIcon"
        tooltipProps={{
          calloutProps: {
            styles: {
              beak: { background: "#252525;" },
              beakCurtain: { background: "#252525;" },
              calloutMain: { background: "#252525;" },
              borderRadius: "20%",
            }, 
          },
        }}
      >
        <span
          className={containerClassName}
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggleRecording}
          style={{ 
            display: "flex", 
            justifyContent: "center",
            cursor: isTalking ? "not-allowed" : "pointer",
            opacity: isTalking ? 0.5 : 1,
            ...(!isLlmMicActive && { width: "100%" }) 
          }}
        >
          {isLlmMicActive ? (
            <MicIconForLlm isRecording={isRecording} />
          ) : (
            <MicIcon isRecording={isRecording} />
          )} 
        </span>
      </CustomToolTipHost>
    </>
  );
};

export default MicToolComponent;